import { useEffect, useRef, useCallback } from 'react';
import { Mic, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import type { AutoTaskPreferences } from '@/types/api';
import { PersonaGrid } from '@/components/voice/PersonaGrid';
import { usePersonaPreferences } from '@/hooks/usePersonaPreferences';
import { speedLabel } from '@/services/settingsService';
import { ConversationArchive } from '@/components/companion/ConversationArchive';

// #2109 R3 L7: speedLabel (services/settingsService) returns an English word;
// the section renders the matching `settings:companion.voice.*` key instead.
const SPEED_KEY = {
  Slower: 'companion.voice.slower',
  Normal: 'companion.voice.normal',
  Faster: 'companion.voice.faster',
} as const;

function VoiceSection() {
  const { t } = useTranslation('settings');
  const { preferences, characters, updatePreferences, isLoading } = usePersonaPreferences();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const handleSpeedChange = useCallback((value: number) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => updatePreferences({ speedAdjustment: value }), 300);
  }, [updatePreferences]);

  if (isLoading) {
    return <p className="text-caption text-stevens-ink-3">{t('companion.voice.loading')}</p>;
  }

  return (
    <div className="space-y-4">
      <PersonaGrid
        characters={characters}
        selectedId={preferences?.persona}
        onSelect={(id) => updatePreferences({ persona: id })}
        showPreview
        compact
      />
      <div>
        <Label className="text-caption font-medium">
          {t('companion.voice.speed', {
            speed: t(SPEED_KEY[speedLabel(preferences?.speedAdjustment ?? 0) as keyof typeof SPEED_KEY] ?? SPEED_KEY.Normal),
          })}
        </Label>
        <input
          type="range"
          aria-label={t('companion.voice.speedAria')}
          min={-0.2}
          max={0.2}
          step={0.05}
          defaultValue={preferences?.speedAdjustment ?? 0}
          onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
          className="w-full mt-1 accent-primary"
        />
        <div className="flex justify-between text-meta text-stevens-ink-3">
          <span>{t('companion.voice.slower')}</span>
          <span>{t('companion.voice.normal')}</span>
          <span>{t('companion.voice.faster')}</span>
        </div>
      </div>
    </div>
  );
}

interface CompanionSectionProps {
  companionName: string;
  // Stevens auto-tasks (parent + autoTasks-gated)
  autoTasks: AutoTaskPreferences | undefined;
  autoSaving: boolean;
  onUpdateAutoTask: (
    payload: Partial<{ enabled: boolean; rules: Partial<{ refill_prep: boolean; post_chemo_recovery: boolean; appointment_prep: boolean }> }>,
  ) => void;
  userRole: string | undefined;
}

export function CompanionSection({
  companionName,
  autoTasks,
  autoSaving,
  onUpdateAutoTask,
  userRole,
}: CompanionSectionProps) {
  const { t } = useTranslation('settings');
  const companion = companionName;
  return (
    <>
      {/* The "Companion Chat" layout card (tabs / split / choice) retired with
          the voice modes it selected (#1731 phase 3): the call floats over the
          thread as the picture-in-picture widget. */}

      {/* ── Conversations ──────────────────────────────────
          The thread archive, moved here from `/conversations` on 2026-09-21
          (Victor: "Move the /conversations to settings"). It is a SECTION of
          this tab rather than a fifth Settings tab: the Companion tab is
          already the companion's settings and the archive is the companion's
          history, so they are one subject — and the mobile `TabsList` is a
          hardcoded `grid-cols-4` that already clips at 320px.

          The quick reach lives on the Today composer's history icon; this is
          the whole list. Parents only — a staff account browsing Settings has
          no family threads to archive. */}
      {userRole === 'parent' && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              <CardTitle>{t('companion.conversations.title')}</CardTitle>
            </div>
            <CardDescription>
              {t('companion.conversations.description', { companion })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ConversationArchive />
          </CardContent>
        </Card>
      )}

      {/* ── Voice ───────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mic className="h-5 w-5 text-primary" />
            <CardTitle>{t('companion.voice.title', { companion })}</CardTitle>
          </div>
          <CardDescription>{t('companion.voice.description', { companion })}</CardDescription>
        </CardHeader>
        <CardContent>
          <VoiceSection />
        </CardContent>
      </Card>

      {/* "Your Companion" free-text name editor removed —
          the coach name is now sourced from the selected voice persona. */}

      {/* The "Companion Preferences" card (Pause-AI + Disable-outbound) was
          removed under D-7 (#642): both toggles persisted values with zero
          enforcement readers — a parent who paused the AI got an AI that
          kept talking. Pause-AI returns only with a designed paused-state
          UX; per-category outbound opt-ins live in the Notifications tab. */}

      {/* ── Stevens auto-tasks ──────────────────────────────── */}
      {userRole === 'parent' && autoTasks && (
      <Card id="stevens-auto-tasks" className="scroll-mt-24">
        <CardHeader>
          <CardTitle>{t('companion.autoTasks.title', { companion })}</CardTitle>
          <CardDescription>
            {t('companion.autoTasks.description', { companion })}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="auto-master" className="text-body font-medium">{t('companion.autoTasks.enable', { companion })}</Label>
              <p className="text-caption text-stevens-ink-3">{t('companion.autoTasks.enableHelper')}</p>
            </div>
            <Switch
              id="auto-master"
              checked={autoTasks.enabled}
              disabled={autoSaving}
              onCheckedChange={(v) => onUpdateAutoTask({ enabled: v })}
            />
          </div>

          <div className={`space-y-3 pl-1 border-l-2 transition-colors ${autoTasks.enabled ? 'border-stevens-indigo/30' : 'border-muted opacity-60'}`}>
            <div className="flex items-start justify-between gap-4 pl-3">
              <div className="space-y-0.5 flex-1 min-w-0">
                <Label htmlFor="auto-refill" className="text-body font-medium">{t('companion.autoTasks.refill')}</Label>
                <p className="text-caption text-stevens-ink-3 italic">{t('companion.autoTasks.refillExample')}</p>
              </div>
              <Switch
                id="auto-refill"
                checked={autoTasks.rules.refill_prep}
                disabled={autoSaving || !autoTasks.enabled}
                onCheckedChange={(v) => onUpdateAutoTask({ rules: { refill_prep: v } })}
              />
            </div>
            <div className="flex items-start justify-between gap-4 pl-3">
              <div className="space-y-0.5 flex-1 min-w-0">
                <Label htmlFor="auto-recovery" className="text-body font-medium">{t('companion.autoTasks.recovery')}</Label>
                <p className="text-caption text-stevens-ink-3 italic">{t('companion.autoTasks.recoveryExample')}</p>
              </div>
              <Switch
                id="auto-recovery"
                checked={autoTasks.rules.post_chemo_recovery}
                disabled={autoSaving || !autoTasks.enabled}
                onCheckedChange={(v) => onUpdateAutoTask({ rules: { post_chemo_recovery: v } })}
              />
            </div>
            <div className="flex items-start justify-between gap-4 pl-3">
              <div className="space-y-0.5 flex-1 min-w-0">
                <Label htmlFor="auto-prep" className="text-body font-medium">{t('companion.autoTasks.prep')}</Label>
                <p className="text-caption text-stevens-ink-3 italic">{t('companion.autoTasks.prepExample')}</p>
              </div>
              <Switch
                id="auto-prep"
                checked={autoTasks.rules.appointment_prep}
                disabled={autoSaving || !autoTasks.enabled}
                onCheckedChange={(v) => onUpdateAutoTask({ rules: { appointment_prep: v } })}
              />
            </div>
          </div>
        </CardContent>
      </Card>
      )}
    </>
  );
}
